import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../services/apiConfig';
import { getToken, handleUnauthorized } from '../services/authApi';

const STORAGE_KEY = 'casapizza_restaurant';
const DEFAULT_ACCENT = '#ef4444';

const RestaurantContext = createContext(null);

const readCached = () => {
  const raw = sessionStorage.getItem(STORAGE_KEY);
  return raw ? JSON.parse(raw) : null;
};

export function RestaurantProvider({ children }) {
  const [restaurant, setRestaurant] = useState(readCached);
  const [domainBranding, setDomainBranding] = useState(null);
  const [loading, setLoading] = useState(() => !!getToken() && !readCached());

  useEffect(() => {
    if (getToken()) return;
    fetch(`${API_BASE_URL}/restaurant/by-domain`)
      .then((res) => (res.ok ? res.json() : null))
      .then(setDomainBranding)
      .catch(() => {});
  }, []);

  const reload = useCallback(async () => {
    const token = getToken();
    if (!token) {
      setRestaurant(null);
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    if (!readCached()) setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/restaurant/config`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        if (res.status === 401) handleUnauthorized();
        return;
      }
      const data = await res.json();
      setRestaurant(data);
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (getToken()) reload();
  }, [reload]);

  useEffect(() => {
    const accent = restaurant?.primary_color || domainBranding?.primary_color || DEFAULT_ACCENT;
    document.documentElement.style.setProperty('--accent', accent);
  }, [restaurant?.primary_color, domainBranding?.primary_color]);

  return (
    <RestaurantContext.Provider value={{ restaurant, domainBranding, loading, reload }}>
      {children}
    </RestaurantContext.Provider>
  );
}

export const useRestaurant = () => useContext(RestaurantContext);
